import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  DollarSign,
  Percent,
  Zap
} from "lucide-react";
import api from "@/lib/api";

const SignalCard = ({ signal, type }) => {
  const isDoThis = type === "do_this";
  
  return (
    <Card 
      data-testid={`signal-card-${signal.symbol}`}
      className={`relative overflow-hidden border-2 transition-all duration-300 hover:scale-[1.02] ${
        isDoThis 
          ? "border-emerald-500/50 bg-gradient-to-br from-emerald-500/10 to-emerald-900/20" 
          : "border-red-500/50 bg-gradient-to-br from-red-500/10 to-red-900/20"
      }`}
    >
      {/* Action badge */}
      <div className={`absolute top-0 right-0 px-4 py-2 text-sm font-black tracking-wider ${
        isDoThis ? "bg-emerald-500 text-black" : "bg-red-500 text-white"
      }`}>
        {isDoThis ? "DO THIS" : "SHORT THIS"}
        {signal.whale_powered && " 🐋"}
      </div>
      
      {/* Signal strength badge */}
      {signal.signal_strength && (
        <div className={`absolute top-0 left-0 px-3 py-2 text-xs font-bold tracking-wider ${
          signal.signal_strength === "STRONG" ? "bg-yellow-500 text-black" :
          signal.signal_strength === "MODERATE" ? "bg-blue-500 text-white" :
          "bg-gray-600 text-gray-200"
        }`}>
          {signal.signal_strength}
        </div>
      )}
      
      <CardHeader className="pb-2 pt-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isDoThis ? "bg-emerald-500/20" : "bg-red-500/20"}`}>
            {isDoThis ? (
              <TrendingUp className="w-8 h-8 text-emerald-400" />
            ) : (
              <TrendingDown className="w-8 h-8 text-red-400" />
            )}
          </div>
          <div>
            <h3 className="text-3xl font-black tracking-tight text-white">{signal.symbol}</h3>
            {signal.company_name && signal.company_name !== signal.symbol && (
              <p className="text-sm text-gray-300">{signal.company_name}</p>
            )}
            <p className={`text-sm font-semibold ${isDoThis ? "text-emerald-400" : "text-red-400"}`}>
              {signal.catalyst || (signal.direction === "UP" ? "EXPECTING UP" : "EXPECTING DOWN")}
            </p>
            {signal.timeframe && (
              <p className="text-xs text-gray-400">{signal.timeframe}</p>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Top option trade - the money maker */}
        {signal.top_option_trade && (
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-400 uppercase tracking-wider mb-1">Top Whale Trade</p>
            <p className="text-sm font-data font-bold text-yellow-300">{signal.top_option_trade}</p>
          </div>
        )}
        
        {/* Price info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Current Price</p>
            <p className="text-2xl font-mono font-bold text-white">
              ${signal.current_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Target Price</p>
            <p className={`text-2xl font-mono font-bold ${isDoThis ? "text-emerald-400" : "text-red-400"}`}>
              ${signal.target_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        
        {/* Confidence meter */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Confidence</span>
            <span className={`text-xl font-black ${
              signal.confidence >= 80 ? "text-yellow-400" : 
              signal.confidence >= 60 ? (isDoThis ? "text-emerald-400" : "text-red-400") : 
              "text-gray-400"
            }`}>
              {signal.confidence}%
            </span>
          </div>
          <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                signal.confidence >= 80 ? "bg-gradient-to-r from-yellow-500 to-yellow-400" :
                signal.confidence >= 60 ? (isDoThis ? "bg-gradient-to-r from-emerald-600 to-emerald-400" : "bg-gradient-to-r from-red-600 to-red-400") :
                "bg-gradient-to-r from-gray-500 to-gray-400"
              }`}
              style={{ width: `${signal.confidence}%` }}
            />
          </div>
        </div>
        
        {/* Reasons */}
        <div className="space-y-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Key Factors</p>
          <div className="space-y-1">
            {signal.reasons?.map((reason, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                <Zap className="w-4 h-4 mt-0.5 text-yellow-500 flex-shrink-0" />
                <span>{reason}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Action button */}
        <Button 
          data-testid={`action-btn-${signal.symbol}`}
          className={`w-full h-12 text-lg font-bold ${
            isDoThis 
              ? "bg-emerald-500 hover:bg-emerald-600 text-black" 
              : "bg-red-500 hover:bg-red-600 text-white"
          }`}
        >
          {isDoThis ? (
            <>
              <ArrowUpRight className="w-5 h-5 mr-2" />
              BUY @ ${signal.current_price?.toFixed(2)}
            </>
          ) : (
            <>
              <ArrowDownRight className="w-5 h-5 mr-2" />
              SHORT @ ${signal.current_price?.toFixed(2)}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const LoadingSkeleton = () => (
  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
    {[1, 2, 3].map(i => (
      <Card key={i} className="border-gray-700">
        <CardHeader>
          <Skeleton className="h-8 w-24 bg-gray-700" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full bg-gray-700" />
          <Skeleton className="h-4 w-full bg-gray-700" />
          <Skeleton className="h-4 w-3/4 bg-gray-700" />
        </CardContent>
      </Card>
    ))}
  </div>
);

export default function SPSignals() {
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forceRefreshing, setForceRefreshing] = useState(false);

  const fetchSignals = async () => {
    try {
      const response = await api.get("/sp-signals");
      setSignals(response.data);
    } catch (error) {
      console.error("Error fetching SP signals:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSignals();
    // Refresh every 30 seconds
    const interval = setInterval(fetchSignals, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSignals();
  };
  
  const handleForceRefresh = async () => {
    setForceRefreshing(true);
    try {
      await api.post("/sp-signals/force-refresh");
      // Wait a bit for the refresh to start, then poll for results
      setTimeout(() => {
        setRefreshing(true);
        fetchSignals();
      }, 2000);
      // Poll again after 30 seconds for full results
      setTimeout(() => {
        fetchSignals();
        setForceRefreshing(false);
      }, 30000);
    } catch (error) {
      console.error("Force refresh failed:", error);
      setForceRefreshing(false);
    }
  };

  const doThisSignals = signals?.do_this || [];
  const shortThisSignals = signals?.short_this || [];
  const totalSignals = doThisSignals.length + shortThisSignals.length;
  const whalePowered = signals?.whale_powered || false;
  const skippedMoved = signals?.skipped_already_moved || 0;

  return (
    <div data-testid="sp-signals-page" className="space-y-8 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight">
            <span className="text-emerald-400">SP</span>{" "}
            <span className="text-white">DO THIS</span>{" "}
            <span className="text-gray-500">/</span>{" "}
            <span className="text-red-400">SHORT THIS</span>
            {whalePowered && <span className="ml-2">🐋</span>}
          </h1>
          <p className="text-gray-400 mt-2">
            {whalePowered 
              ? "Powered by Unusual Whales - Catching stocks BEFORE they move"
              : "Predictive v2.0 - Looking for setups, not chasing moves"
            }
          </p>
          {signals?.version && (
            <p className="text-xs text-gray-500 mt-1">{signals.version}</p>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <Button
            data-testid="force-refresh-btn"
            variant="default"
            onClick={handleForceRefresh}
            disabled={forceRefreshing}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Zap className={`w-4 h-4 mr-2 ${forceRefreshing ? "animate-pulse" : ""}`} />
            {forceRefreshing ? "Scanning..." : "Force Refresh"}
          </Button>
          
          <Button
            data-testid="refresh-btn"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-gray-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          
          {signals?.scan_time && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="w-4 h-4" />
              {new Date(signals.scan_time).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-gray-700 bg-gray-800/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{doThisSignals.length}</p>
              <p className="text-xs text-gray-400">DO THIS</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-700 bg-gray-800/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{shortThisSignals.length}</p>
              <p className="text-xs text-gray-400">SHORT THIS</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-700 bg-gray-800/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Target className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{signals?.total_scanned || 0}</p>
              <p className="text-xs text-gray-400">Scanned</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-700 bg-gray-800/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20">
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-400">{totalSignals}</p>
              <p className="text-xs text-gray-400">Signals Found</p>
            </div>
          </CardContent>
        </Card>
        
        {skippedMoved > 0 && (
          <Card className="border-gray-700 bg-gray-800/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-500/20">
                <Clock className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-400">{skippedMoved}</p>
                <p className="text-xs text-gray-400">Skipped (moved)</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Whale Powered Banner */}
      {whalePowered && (
        <Card className="border-cyan-500/30 bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
          <CardContent className="p-4 flex items-center gap-4">
            <span className="text-4xl">🐋</span>
            <div>
              <p className="text-cyan-400 font-bold">Whale Powered by Unusual Whales</p>
              <p className="text-gray-400 text-sm">
                Real-time institutional options flow data • Sweeps • Large block trades
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {loading && <LoadingSkeleton />}

      {/* Signals still loading message */}
      {!loading && signals?.is_loading && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="p-6 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-4 text-yellow-400 animate-spin" />
            <p className="text-yellow-400 font-semibold">
              {signals.message || "Scanning market for signals..."}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              The AI is analyzing stocks. Please refresh in a moment.
            </p>
          </CardContent>
        </Card>
      )}

      {/* DO THIS Section */}
      {!loading && !signals?.is_loading && doThisSignals.length > 0 && (
        <section data-testid="do-this-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-400">DO THIS</h2>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
              BUY
            </Badge>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {doThisSignals.map((signal) => (
              <SignalCard key={signal.symbol} signal={signal} type="do_this" />
            ))}
          </div>
        </section>
      )}

      {/* SHORT THIS Section */}
      {!loading && !signals?.is_loading && shortThisSignals.length > 0 && (
        <section data-testid="short-this-section" className="mt-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-red-500/20">
              <TrendingDown className="w-6 h-6 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-red-400">SHORT THIS</h2>
            <Badge className="bg-red-500/20 text-red-400 border-red-500/50">
              SHORT
            </Badge>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {shortThisSignals.map((signal) => (
              <SignalCard key={signal.symbol} signal={signal} type="short_this" />
            ))}
          </div>
        </section>
      )}

      {/* No signals */}
      {!loading && !signals?.is_loading && totalSignals === 0 && (
        <Card className="border-gray-700">
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 mx-auto mb-4 text-gray-500" />
            <h3 className="text-xl font-semibold text-gray-300">No Clear Signals Right Now</h3>
            <p className="text-gray-500 mt-2">
              The market doesn't have strong directional momentum. Check back soon.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Disclaimer */}
      <Card className="border-gray-700/50 bg-gray-900/50">
        <CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500">
            ⚠️ These signals are for educational purposes only. Always do your own research before trading.
            Past performance does not guarantee future results.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
